import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { clsx } from 'clsx';
import {
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { BillingStatementListItem } from '@/types';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { paidDiscrepancy } from '../utils/paid-discrepancy';
import { formatStatementPeriod } from '../utils/statement-period';

// TODO: migrate to formatDecimalString (utils/decimal-display) — this float
// path predates HON-03 and renders symbol/spacing slightly differently from
// the string-safe Paid Amount column in the same row.
const formatAmount = (value: string, currency: string): string => {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// No 'issued': no app action sets it (LR-681); a row set by hand in the admin falls
// back to the draft style.
const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  paid: 'bg-green-100 text-green-700',
};

const AGREEMENT_STYLES: Record<string, string> = {
  gmsla: 'bg-blue-100 text-blue-800',
  gmra: 'bg-purple-100 text-purple-800',
  // LR-558: a cash pool interest statement carries a blank `agreement` (the pool
  // backs both GMSLA and GMRA), so the pill reads its document type instead.
  cash_pool_interest: 'bg-teal-100 text-teal-800',
};

/**
 * What the Type pill says and is styled by. A trade statement is identified by
 * its master agreement (GMSLA / GMRA); a cash pool statement has none, so it is
 * identified by its document type. Reading `agreement_display` alone would
 * render the pool row's pill empty.
 *
 * The pool pill is SHORTENED rather than showing `statement_type_display`
 * verbatim: the cell is a fixed-width `overflow-hidden` flex item, and
 * "CASH POOL INTEREST" uppercased is wider than the column, so the server's full
 * label was being clipped mid-word. `title` keeps the full name reachable, and
 * the document page header spells it out in full.
 */
const typePill = (
  row: BillingStatementListItem,
): { key: string; label: string; title?: string } =>
  row.statement_type === 'cash_pool_interest'
    ? {
        key: 'cash_pool_interest',
        label: 'Cash Pool',
        title: row.statement_type_display || 'Cash Pool Interest',
      }
    : { key: row.agreement, label: row.agreement_display };

export interface BillingColumnOptions {
  /** Row-level Mark paid action (LR-556); the column renders only when set. */
  onMarkPaid?: (row: BillingStatementListItem) => void;
  /** Per-security expansion (LR-557); the chevron column renders only when set. */
  expansion?: {
    isExpanded: (id: number) => boolean;
    onToggle: (id: number) => void;
  };
}

export const createBillingColumns = (
  options: BillingColumnOptions = {},
): ColumnDef<BillingStatementListItem>[] => [
  ...(options.expansion
    ? [
        {
          id: 'expander',
          header: '',
          size: 26,
          cell: (info) => {
            const r = info.row.original;
            const expanded = options.expansion!.isExpanded(r.id);
            return (
              <button
                type="button"
                aria-expanded={expanded}
                // The panel exists only while expanded, so the reference is
                // conditional — a dangling aria-controls id is a defect too.
                aria-controls={expanded ? `stmt-${r.id}-securities` : undefined}
                aria-label={`${expanded ? 'Hide' : 'Show'} per-security accruals for ${r.counterparty_name} ${r.currency}`}
                onClick={(e) => {
                  // Row click navigates to the document page — keep the
                  // expansion toggle local to the chevron.
                  e.stopPropagation();
                  options.expansion!.onToggle(r.id);
                }}
                className="inline-flex text-gray-400 hover:text-blue-600"
              >
                {expanded ? (
                  <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </button>
            );
          },
        } satisfies ColumnDef<BillingStatementListItem>,
      ]
    : []),
  {
    accessorKey: 'counterparty_name',
    header: 'Counterparty',
    size: 220,
    cell: (info) => (
      <span className="font-medium text-gray-900 truncate">
        {info.getValue() as string}
      </span>
    ),
  },
  {
    id: 'period',
    header: 'Period',
    size: 220,
    cell: (info) => {
      const r = info.row.original;
      return (
        <span className="text-xs text-gray-700">
          {formatStatementPeriod(r.period_start, r.period_end)}
        </span>
      );
    },
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    size: 90,
    cell: (info) => (
      <span className="text-xs font-mono text-gray-700">
        {info.getValue() as string}
      </span>
    ),
  },
  {
    accessorKey: 'agreement',
    // 'Type' (LR-557 / feedback #74): the statement's document type as the
    // trader reads it — the GMSLA/GMRA pill, or the Cash Pool Interest pill for
    // a pool statement (LR-558). The accessor stays `agreement`.
    header: 'Type',
    size: 124,
    cell: (info) => {
      const { key, label, title } = typePill(info.row.original);
      return (
        <span
          title={title}
          className={clsx(
            'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium uppercase',
            AGREEMENT_STYLES[key] ?? 'bg-gray-100 text-gray-700',
          )}
        >
          {/* Only where the label was actually shortened, and as TEXT rather
              than `aria-label`: this span has no role, and ARIA prohibits a name
              on the generic role — most screen readers drop it, so the pill
              would be announced as the abbreviation only. (The icon-only Note
              column below solves the same problem with role="img"; here there is
              real text to expose.) `title` keeps the mouse tooltip. */}
          {title != null && title !== label ? (
            <>
              <span className="sr-only">{title}</span>
              <span aria-hidden="true">{label}</span>
            </>
          ) : (
            label
          )}
        </span>
      );
    },
  },
  {
    accessorKey: 'total_accrued',
    header: 'Total Accrued',
    size: 160,
    cell: (info) => {
      const r = info.row.original;
      return (
        <span className="text-sm tabular-nums text-gray-900">
          {formatAmount(r.total_accrued, r.currency)}
        </span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 110,
    cell: (info) => {
      const v = (info.getValue() as string) || '';
      return (
        <span
          className={clsx(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_STYLES[v] ?? STATUS_STYLES.draft,
          )}
        >
          {info.row.original.status_display}
        </span>
      );
    },
  },
  {
    accessorKey: 'paid_at',
    header: 'Paid',
    size: 120,
    cell: (info) => {
      const v = info.getValue() as string | null;
      // Mock doctrine: a recorded paid date reads green, absence is a muted dash.
      return (
        <span
          className={clsx(
            'text-xs tabular-nums',
            v ? 'text-green-700' : 'text-gray-400',
          )}
        >
          {v ? v.slice(0, 10) : '—'}
        </span>
      );
    },
  },
  {
    accessorKey: 'paid_amount',
    header: 'Paid Amount',
    size: 150,
    cell: (info) => {
      const r = info.row.original;
      // toDecimalString: consumption-boundary guard (HON-03) — the field is
      // typed string but can arrive as a number at runtime.
      const v = toDecimalString(info.getValue() as string | number | null);
      if (!v) return <span className="text-xs text-gray-400">—</span>;
      // LR-556 AC: a mismatch with the accrued figure must be visually
      // noticeable — amber + a tooltip carrying the exact difference. A clean
      // match follows the mock's signed money colors (+ green / − red).
      const diff = paidDiscrepancy(r.total_accrued, v);
      const negative = v.startsWith('-');
      return (
        <span
          className={clsx(
            'text-sm tabular-nums',
            diff
              ? 'font-semibold text-amber-700'
              : negative
                ? 'text-red-600'
                : 'text-green-700',
          )}
          title={
            diff
              ? `Differs from accrued by ${formatDecimalString(diff, { currency: r.currency })}`
              : undefined
          }
        >
          {formatDecimalString(v, { currency: r.currency })}
        </span>
      );
    },
  },
  {
    accessorKey: 'notes',
    header: 'Note',
    size: 60,
    cell: (info) => {
      const v = (info.getValue() as string) || '';
      if (!v) return null;
      // Icon-only column (LR-556 / T23): the note text lives in the tooltip
      // and in the detail modal.
      return (
        // role="img" makes the aria-label announceable (a bare span's label is
        // ignored by most screen readers); title keeps the mouse tooltip.
        <span
          role="img"
          title={v}
          aria-label={`Note: ${v}`}
          className="inline-flex text-gray-500"
        >
          <ChatBubbleBottomCenterTextIcon className="h-4 w-4" aria-hidden="true" />
        </span>
      );
    },
  },
  ...(options.onMarkPaid
    ? [
        {
          id: 'actions',
          header: '',
          size: 110,
          cell: (info) => {
            const r = info.row.original;
            if (r.status === 'paid') return null;
            return (
              <button
                type="button"
                onClick={(e) => {
                  // Row click opens the detail modal — keep the action local.
                  e.stopPropagation();
                  options.onMarkPaid?.(r);
                }}
                // Mock row-action style: subtle green on white.
                className="rounded border border-[#cde9d6] bg-white px-2 py-0.5 text-xs font-medium text-green-700 hover:bg-[#ecf6ef]"
              >
                Mark paid
              </button>
            );
          },
        } satisfies ColumnDef<BillingStatementListItem>,
      ]
    : []),
];
